import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { BleConnectionLog } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminBleConnectionLogListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<APIList<BleConnectionLog>, Error>,
    'queryKey' | 'queryFn'
  >;
  enabled?: boolean
}

interface AdminBleConnectionLogListGetParams {
  limit: number;
  offset: number;
  sort: string;
  contractorId: number;
  projectId: number;
  eventType: BleConnectionLogEventType;
  probeRawId: string;
  appVersion: string;
  deviceOS: string;
  search: string;
}

type BleConnectionLogEventType = Parameters<
  ReturnType<typeof useApiInstance>['v1AdminLogsBleConnectionLogsGet']
>[5]

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  contractorId: 'number',
  projectId: 'number',
  eventType: 'string',
  probeRawId: 'string',
  appVersion: 'string',
  deviceOS: 'string',
  search: 'string',
}

export const useBleConnectionLogListRequest = ({
  params = {},
  paramsTransform = [],
  options,
  enabled = true,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminBleConnectionLogListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<APIList<BleConnectionLog>, Error>(
    [Entities.BleConnectionLogs, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, contractorId, projectId, eventType, probeRawId, appVersion, deviceOS, search } = queryParams
      const { data } = await api.v1AdminLogsBleConnectionLogsGet(
        limit,
        offset,
        sort,
        contractorId,
        projectId,
        eventType,
        probeRawId,
        appVersion,
        deviceOS,
        search,
      )
      return data as APIList<BleConnectionLog>
    },
    {
      enabled: Object.keys(transformedParams).length > 0 && enabled,
      ...options,
    },
  )
}
